import { useContext } from 'react';
import { useSelector } from 'react-redux';

import { L10nContext } from 'context/L10nContext';

import { AsyncReducerStatus } from 'store/common/AsyncReducerStatus';
import { useTypedSelector } from 'store/common/TypedSelector';

import { ModalDialogueHeader } from 'presentation/ui/compositions/modal-dialogue/modal-dialogue-header/ModalDialogueHeader';
import { ModalDialogueSection } from 'presentation/ui/compositions/modal-dialogue/modal-dialogue-section/ModalDialogueSection';
import { ModalDialogue } from 'presentation/ui/compositions/modal-dialogue/ModalDialogue';
import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { selectCreatedWasteManagementReport } from '../../../../store/wasteManagementReportSlice';
import {
	WasteManagementCyclotronReportCreateForm
} from '../waste-management-cyclotron-report-create-form/WasteManagementCyclotronReportCreateForm';
import { WasteManagementCyclotronReportCreateFailedNotification } from './WasteManagementCyclotronReportCreateFailedNotification';
import { WasteManagementCyclotronReportCreateSuccessNotification } from './WasteManagementCyclotronReportCreateSuccessNotification';

interface WasteManagementCyclotronReportCreateModalProps {
	/**
	 * onDismiss
	 */
	onDismiss: () => void;
}

/**
 * The report create modal
 */
export const WasteManagementCyclotronReportCreateModal = (props: WasteManagementCyclotronReportCreateModalProps): JSX.Element => {
	const { onDismiss } = props;

	// Consume the context
	const l10nContext = useContext(L10nContext);

	// Use selector to provide information from store
	const createdWasteManagementReport = useSelector(selectCreatedWasteManagementReport());
	const wasteManagementReportActionStatus = useTypedSelector(state => state.wasteManagementReports.actionStatus);

	const renderModal = () => {
		let modal: JSX.Element = null;

		switch (wasteManagementReportActionStatus) {
			case AsyncReducerStatus.IDLE:
				modal = <WasteManagementCyclotronReportCreateForm />;
				break;

			case AsyncReducerStatus.CREATED:
				modal =
					<WasteManagementCyclotronReportCreateSuccessNotification
						onClickConfirm={onDismiss}
						wasteManagementReportCreated={createdWasteManagementReport}
					/>;
				break;

			case AsyncReducerStatus.FAILED:
				modal =
					<WasteManagementCyclotronReportCreateFailedNotification
						onClickConfirm={onDismiss}
					/>;
				break;
		}
		return modal;
	};

	return (
		<ModalDialogue onDismiss={onDismiss}>
			<ModalDialogueHeader
				captionText={l10nContext.translate('view.wastemanagementReports.modal.action.create.caption', 'Jahresmeldung hinzufügen')}
				captionIcon={IconIdentifier.ADD}
			/>
			<ModalDialogueSection>
				{renderModal()}
			</ModalDialogueSection>
		</ModalDialogue>
	);
};
